import * as THREE from 'three';
import { BlockId } from '../world/BlockType.js';
import { isTool } from './ToolSystem.js';
import { getItemColor } from './ItemRegistry.js';
import { colorDe, rarezaPorId } from './Rareza.js';
import { MAX_STACK_SIZE } from '../constants.js';

// --- Item drop physics & behavior constants ---
const MAX_DROPS = 200;                    // Maximum number of drops in the world at once
const DROP_CUBE_SIZE = 0.25;              // Size of the dropped item cube (each axis)
const DROP_CUBE_HALF = DROP_CUBE_SIZE / 2;// Half the cube size, used for ground collision offsets
const BLOCK_CENTER_OFFSET = 0.5;          // Offset to center drop within the block

// Pop-out animation when blocks break
const POP_HORIZONTAL_SPEED = 3;           // Max horizontal velocity of pop-out
const POP_UPWARD_BASE = 4;               // Base upward velocity of pop-out
const POP_UPWARD_RANDOM = 2;             // Random additional upward velocity
const PICKUP_DELAY_ANIMATED = 0.5;        // Seconds before animated drops can be picked up

// Gravity & movement
const GRAVITY = 20;                       // Downward acceleration (blocks/s^2)
const TERMINAL_VELOCITY = -30;            // Max downward speed (blocks/s)
const HORIZONTAL_FRICTION = 3;            // Friction coefficient for horizontal velocity

// Despawn & void
const DESPAWN_TIME = 300;                 // Seconds before drops disappear (5 minutes)
const VOID_Y_THRESHOLD = -10;            // Y below which drops are removed

// Bobbing & rotation animation
const SPIN_SPEED = 2;                     // Rotation speed (radians/s)
const BOB_FREQUENCY = 2;                  // Bobbing oscillation speed
const BOB_AMPLITUDE = 0.1;               // Bobbing oscillation height (blocks)

// Pickup & attraction
const PICKUP_RADIUS = 1.5;               // Distance at which items are picked up
const MAGNET_RADIUS = 2.0;               // Distance at which magnetic attraction starts
const MAGNET_MIN_DIST = 0.1;             // Minimum distance for magnetic attraction (avoid division issues)
const MAGNET_SPEED = 5;                  // Speed of magnetic pull toward player (blocks/s)
const PLAYER_CENTER_Y_OFFSET = 0.9;      // Offset from player pos to player center for distance check

/**
 * ItemDropManager - manages dropped items in the world
 *
 * Dropped items are small rotating cubes that float slightly above ground.
 * Player picks them up by walking near (PICKUP_RADIUS block radius).
 * Items despawn after DESPAWN_TIME seconds.
 */
export class ItemDropManager {
  constructor(scene) {
    this.scene = scene;
    this.drops = []; // Array of drop objects
    this.maxDrops = MAX_DROPS;
    this._geometry = new THREE.BoxGeometry(DROP_CUBE_SIZE, DROP_CUBE_SIZE, DROP_CUBE_SIZE);
    this._clock = 0; // Global clock for bobbing offsets
  }

  /**
   * Drop an item at a position (e.g., when block is broken)
   * @param {number} blockId - The block type to drop
   * @param {number} count - How many to drop
   * @param {THREE.Vector3} position - Where to drop
   * @param {boolean} animate - Whether to pop out with velocity (true for block breaks)
   */
  dropItem(blockId, count, position, animate = true) {
    if (blockId === BlockId.AIR || blockId === BlockId.BEDROCK) return;

    // Normalize open doors to closed form for drops
    if (blockId === BlockId.DOOR_OPEN) blockId = BlockId.DOOR_CLOSED;

    const color = this._getBlockColor(blockId);
    const material = new THREE.MeshLambertMaterial({ color });
    const mesh = new THREE.Mesh(this._geometry, material);

    mesh.position.set(position.x + BLOCK_CENTER_OFFSET, position.y + BLOCK_CENTER_OFFSET, position.z + BLOCK_CENTER_OFFSET);
    mesh.castShadow = false;
    mesh.receiveShadow = false;

    const velocity = new THREE.Vector3(0, 0, 0);
    if (animate) {
      // Random upward + sideways velocity for a "pop out" effect
      velocity.set(
        (Math.random() - 0.5) * POP_HORIZONTAL_SPEED,
        POP_UPWARD_BASE + Math.random() * POP_UPWARD_RANDOM,
        (Math.random() - 0.5) * POP_HORIZONTAL_SPEED
      );
    }

    this.scene.add(mesh);

    const drop = {
      mesh,
      blockId,
      count,
      item: { blockId, count },
      isTool: false,
      velocity,
      age: 0,
      bobOffset: Math.random() * Math.PI * 2, // Randomize bob phase
      grounded: false,
      groundY: 0, // Y position of the ground surface
      pickupDelay: animate ? PICKUP_DELAY_ANIMATED : 0, // Brief delay before pickup allowed
    };

    this.drops.push(drop);

    // Enforce max drops limit - remove oldest first
    while (this.drops.length > this.maxDrops) {
      const oldest = this.drops.shift();
      this.scene.remove(oldest.mesh);
      oldest.mesh.material.dispose();
    }

    return drop;
  }

  /**
   * Drop a non-block item (negative ID) at a position
   * @param {number} itemId - The negative item ID
   * @param {number} count - How many to drop
   * @param {THREE.Vector3} position - Where to drop
   * @param {boolean} animate - Whether to pop out with velocity
   */
  /**
   * @param {object|null} rareza - lo que devuelve tirarObjeto(): { rareza, bonos }.
   *        Si viene, el objeto cae con su color, su halo y su haz de luz.
   */
  dropNonBlockItem(itemId, count, position, animate = true, rareza = null) {
    const hexColor = getItemColor(itemId);
    const color = hexColor ? parseInt(hexColor.replace('#', '0x'), 16) : 0x888888;
    const material = new THREE.MeshLambertMaterial({ color });
    const mesh = new THREE.Mesh(this._geometry, material);
    if (rareza && rareza.rareza && rareza.rareza !== 'comun') {
      this._vestirDeRareza(mesh, rareza);
    }

    mesh.position.set(position.x + BLOCK_CENTER_OFFSET, position.y + BLOCK_CENTER_OFFSET, position.z + BLOCK_CENTER_OFFSET);

    const velocity = new THREE.Vector3(0, 0, 0);
    if (animate) {
      velocity.set(
        (Math.random() - 0.5) * POP_HORIZONTAL_SPEED,
        POP_UPWARD_BASE + Math.random() * POP_UPWARD_RANDOM,
        (Math.random() - 0.5) * POP_HORIZONTAL_SPEED
      );
    }

    this.scene.add(mesh);

    const drop = {
      mesh,
      blockId: null,
      count,
      item: { itemId, count, ...(rareza || {}) },
      isTool: false,
      isNonBlock: true,
      itemId,
      rareza: rareza ? rareza.rareza : null,
      velocity,
      age: 0,
      bobOffset: Math.random() * Math.PI * 2,
      grounded: false,
      groundY: 0,
      pickupDelay: animate ? PICKUP_DELAY_ANIMATED : 0,
    };

    this.drops.push(drop);

    while (this.drops.length > this.maxDrops) {
      const oldest = this.drops.shift();
      this.scene.remove(oldest.mesh);
      oldest.mesh.material.dispose();
    }

    return drop;
  }

  /* ==================================================================
     EL BOTÍN SE TIENE QUE VER

     Una legendaria que cae detrás de una piedra y no se distingue de la
     tierra no es un premio, es una trampa. Tres capas, y las tres cuestan
     casi nada porque medimos que la GPU está ociosa —8,1 ms clavados al
     vsync en un M3, sin moverse aunque bajen los píxeles siete veces—:

       1. el cubo se tiñe del color de su rareza,
       2. un halo translúcido alrededor,
       3. un haz de luz vertical que se lee desde lejos.

     El común NO lleva nada: si todo brilla, no brilla nada.
     ================================================================== */
  _vestirDeRareza(mesh, objeto) {
    const hex = parseInt(colorDe(objeto).replace('#', '0x'), 16);
    const r = rarezaPorId(objeto.rareza);
    mesh.material.color.setHex(hex);
    /* emissive: en un Lambert es lo que hace que se vea en una cueva oscura,
       que es justo donde caen las cosas buenas. */
    mesh.material.emissive = new THREE.Color(hex);
    mesh.material.emissiveIntensity = 0.6;

    const halo = new THREE.Mesh(
      this._geometry,
      new THREE.MeshBasicMaterial({ color: hex, transparent: true, opacity: 0.22,
                                    depthWrite: false, side: THREE.BackSide }),
    );
    halo.scale.setScalar(2.1);
    mesh.add(halo);

    /* El haz solo desde «rara» hacia arriba: es lo que hace que Fede cruce el
       mapa a mirar qué ha caído, y si sale por todo pierde el sentido. */
    if (r.mult >= 1.35) {
      const alto = 2.2 + (r.mult - 1.35) * 2;
      const haz = new THREE.Mesh(
        new THREE.CylinderGeometry(0.16, 0.05, alto, 8, 1, true),
        new THREE.MeshBasicMaterial({ color: hex, transparent: true, opacity: 0.30,
                                      depthWrite: false, side: THREE.DoubleSide }),
      );
      haz.position.y = alto / 2;
      mesh.add(haz);
      mesh.userData.haz = haz;
    }
    mesh.userData.rareza = objeto.rareza;
  }

  /**
   * Drop a tool item at a position
   * @param {object} toolItem - The tool object {toolId, toolType, material, durability, maxDurability}
   * @param {THREE.Vector3} position - Where to drop
   * @param {boolean} animate - Whether to pop out with velocity
   */
  dropTool(toolItem, position, animate = true) {
    if (!toolItem) return;

    const color = this._getToolColor(toolItem.material);
    const material = new THREE.MeshLambertMaterial({ color });
    const mesh = new THREE.Mesh(this._geometry, material);

    mesh.position.set(position.x + BLOCK_CENTER_OFFSET, position.y + BLOCK_CENTER_OFFSET, position.z + BLOCK_CENTER_OFFSET);
    mesh.castShadow = false;
    mesh.receiveShadow = false;

    const velocity = new THREE.Vector3(0, 0, 0);
    if (animate) {
      velocity.set(
        (Math.random() - 0.5) * POP_HORIZONTAL_SPEED,
        POP_UPWARD_BASE + Math.random() * POP_UPWARD_RANDOM,
        (Math.random() - 0.5) * POP_HORIZONTAL_SPEED
      );
    }

    this.scene.add(mesh);

    const drop = {
      mesh,
      blockId: null,
      count: 1,
      item: { ...toolItem },
      isTool: true,
      velocity,
      age: 0,
      bobOffset: Math.random() * Math.PI * 2,
      grounded: false,
      groundY: 0,
      pickupDelay: animate ? PICKUP_DELAY_ANIMATED : 0,
    };

    this.drops.push(drop);

    // Enforce max drops limit
    while (this.drops.length > this.maxDrops) {
      const oldest = this.drops.shift();
      this.scene.remove(oldest.mesh);
      oldest.mesh.material.dispose();
    }

    return drop;
  }

  /**
   * Update all drops: apply gravity, bobbing animation, age, and check pickup
   * @param {number} dt - Delta time in seconds
   * @param {THREE.Vector3} playerPos - Player position for pickup detection
   * @param {object} inventory - Player inventory to add items to
   * @param {object} world - World for ground collision
   */
  update(dt, playerPos, inventory, world) {
    this._clock += dt;

    for (let i = this.drops.length - 1; i >= 0; i--) {
      const drop = this.drops[i];

      // Increment age
      drop.age += dt;

      // Decrease pickup delay
      if (drop.pickupDelay > 0) {
        drop.pickupDelay -= dt;
      }

      // Despawn after DESPAWN_TIME seconds
      if (drop.age > DESPAWN_TIME) {
        this._removeDrop(i);
        continue;
      }

      // --- Physics: gravity and ground collision ---
      if (!drop.grounded) {
        // Apply gravity
        drop.velocity.y -= GRAVITY * dt;

        // Clamp terminal velocity
        if (drop.velocity.y < TERMINAL_VELOCITY) drop.velocity.y = TERMINAL_VELOCITY;

        // Move by velocity
        drop.mesh.position.x += drop.velocity.x * dt;
        drop.mesh.position.y += drop.velocity.y * dt;
        drop.mesh.position.z += drop.velocity.z * dt;

        // Apply horizontal friction
        drop.velocity.x *= (1 - HORIZONTAL_FRICTION * dt);
        drop.velocity.z *= (1 - HORIZONTAL_FRICTION * dt);

        // Check ground collision
        const bx = Math.floor(drop.mesh.position.x);
        const by = Math.floor(drop.mesh.position.y - DROP_CUBE_HALF); // Check half-height below center
        const bz = Math.floor(drop.mesh.position.z);

        if (world) {
          const blockBelow = world.getBlock(bx, by, bz);
          const blockAt = world.getBlock(bx, Math.floor(drop.mesh.position.y), bz);

          if (blockBelow !== BlockId.AIR && blockBelow !== BlockId.WATER &&
              blockBelow !== BlockId.DOOR_OPEN &&
              (blockAt === BlockId.AIR || blockAt === BlockId.WATER ||
              blockAt === BlockId.DOOR_OPEN)) {
            // Land on top of the block below
            if (drop.velocity.y < 0) {
              drop.grounded = true;
              drop.groundY = by + 1 + DROP_CUBE_HALF; // Rest on top of block + half cube height
              drop.mesh.position.y = drop.groundY;
              drop.velocity.set(0, 0, 0);
            }
          }

          // Safety: prevent falling into the void
          if (drop.mesh.position.y < VOID_Y_THRESHOLD) {
            this._removeDrop(i);
            continue;
          }
        }
      } else {
        // Grounded: check if ground was removed (block broken below)
        if (world) {
          const bx = Math.floor(drop.mesh.position.x);
          const by = Math.floor(drop.groundY - DROP_CUBE_SIZE);
          const bz = Math.floor(drop.mesh.position.z);
          const blockBelow = world.getBlock(bx, by, bz);
          if (blockBelow === BlockId.AIR || blockBelow === BlockId.WATER ||
              blockBelow === BlockId.DOOR_OPEN) {
            // Ground removed, start falling again
            drop.grounded = false;
            drop.velocity.set(0, 0, 0);
          }
        }
      }

      // --- Rotation: slow spin ---
      drop.mesh.rotation.y += dt * SPIN_SPEED;

      // --- Bobbing animation (only when grounded) ---
      if (drop.grounded) {
        /* El haz late y gira despacio. Girar con el cubo lo haría parecer una
           pieza más del objeto; girando al revés y a otra velocidad se lee como
           luz, que es lo que es. */
        if (drop.mesh.userData.haz) {
          const h = drop.mesh.userData.haz;
          h.rotation.y = -drop.mesh.rotation.y * 0.4 + this._clock * 0.6;
          h.material.opacity = 0.22 + Math.sin(this._clock * 2.4 + drop.bobOffset) * 0.09;
        }

        const bob = Math.sin(this._clock * BOB_FREQUENCY + drop.bobOffset) * BOB_AMPLITUDE;
        drop.mesh.position.y = drop.groundY + bob;
      }

      // --- Pickup logic ---
      if (drop.pickupDelay <= 0 && playerPos) {
        const dx = drop.mesh.position.x - playerPos.x;
        const dy = drop.mesh.position.y - (playerPos.y + PLAYER_CENTER_Y_OFFSET); // Check vs player center
        const dz = drop.mesh.position.z - playerPos.z;
        const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);

        // Magnetic attraction when within MAGNET_RADIUS blocks
        if (dist < MAGNET_RADIUS && dist > MAGNET_MIN_DIST) {
          const factor = MAGNET_SPEED * dt / dist;
          drop.mesh.position.x -= dx * factor;
          drop.mesh.position.y -= dy * factor;
          drop.mesh.position.z -= dz * factor;
          // Unground so it can float toward player
          if (drop.grounded) {
            drop.grounded = false;
            drop.velocity.set(0, 0, 0);
          }
        }

        // Pickup when very close
        if (dist < PICKUP_RADIUS) {
          if (this._tryPickup(drop, inventory)) {
            this._removeDrop(i);
            continue;
          }
        }
      }
    }
  }

  /**
   * Try to add item to inventory, return true if successful
   * @param {object} drop - The drop to pick up
   * @param {object} inventory - The player inventory
   * @returns {boolean} - Whether the item was picked up
   */
  _tryPickup(drop, inventory) {
    if (!inventory) return false;

    if (drop.isTool) {
      // Tools go into the first empty slot (hotbar first, then storage)
      for (let i = 0; i < inventory.slots.length; i++) {
        if (!inventory.slots[i]) {
          inventory.slots[i] = { ...drop.item };
          return true;
        }
      }
      for (let i = 0; i < inventory.storage.length; i++) {
        if (!inventory.storage[i]) {
          inventory.storage[i] = { ...drop.item };
          return true;
        }
      }
      return false; // No empty slot
    }

    // Non-block items: use itemId and count format
    if (drop.isNonBlock && drop.itemId !== undefined) {
      return inventory.addItem(drop.itemId, drop.count);
    }

    // Block items: use blockId and count format
    const blockId = drop.blockId;
    let remaining = drop.count;

    // Try stacking in hotbar
    for (let i = 0; i < inventory.slots.length; i++) {
      if (remaining <= 0) break;
      const slot = inventory.slots[i];
      if (slot && slot.blockId === blockId && !isTool(slot) && slot.count < MAX_STACK_SIZE) {
        const canAdd = Math.min(remaining, MAX_STACK_SIZE - slot.count);
        slot.count += canAdd;
        remaining -= canAdd;
      }
    }

    // Try stacking in storage
    for (let i = 0; i < inventory.storage.length; i++) {
      if (remaining <= 0) break;
      const slot = inventory.storage[i];
      if (slot && slot.blockId === blockId && !isTool(slot) && slot.count < MAX_STACK_SIZE) {
        const canAdd = Math.min(remaining, MAX_STACK_SIZE - slot.count);
        slot.count += canAdd;
        remaining -= canAdd;
      }
    }

    // Try empty slots in hotbar
    for (let i = 0; i < inventory.slots.length; i++) {
      if (remaining <= 0) break;
      if (!inventory.slots[i]) {
        const place = Math.min(remaining, MAX_STACK_SIZE);
        inventory.slots[i] = { blockId, count: place };
        remaining -= place;
      }
    }

    // Try empty slots in storage
    for (let i = 0; i < inventory.storage.length; i++) {
      if (remaining <= 0) break;
      if (!inventory.storage[i]) {
        const place = Math.min(remaining, MAX_STACK_SIZE);
        inventory.storage[i] = { blockId, count: place };
        remaining -= place;
      }
    }

    if (remaining < drop.count) {
      // At least some items were picked up
      if (remaining > 0) {
        // Partial pickup: update remaining count
        drop.count = remaining;
        drop.item.count = remaining;
        return false; // Not fully picked up
      }
      return true; // Fully picked up
    }

    return false; // Inventory full, nothing picked up
  }

  /**
   * Remove a drop at the given index
   * @param {number} index - Index in the drops array
   */
  _removeDrop(index) {
    const drop = this.drops[index];
    this.scene.remove(drop.mesh);
    drop.mesh.material.dispose();
    this.drops.splice(index, 1);
  }

  /**
   * Remove all drops (e.g., on world clear)
   */
  clear() {
    for (const drop of this.drops) {
      this.scene.remove(drop.mesh);
      drop.mesh.material.dispose();
    }
    this.drops.length = 0;
  }

  /**
   * Get the number of active drops
   * @returns {number}
   */
  getDropCount() {
    return this.drops.length;
  }

  /**
   * Get block color for the dropped item cube
   * @param {number} blockId - The block type ID
   * @returns {number} - Hex color value
   */
  _getBlockColor(blockId) {
    switch (blockId) {
      case BlockId.GRASS:       return 0x5B8C2A;
      case BlockId.DIRT:        return 0x8B6B3E;
      case BlockId.STONE:       return 0x8A8A8A;
      case BlockId.SAND:        return 0xD4C483;
      case BlockId.WOOD:        return 0x6B4F2E;
      case BlockId.LEAVES:      return 0x3A7A1A;
      case BlockId.WATER:       return 0x3366AA;
      case BlockId.SNOW_GRASS:  return 0xF0F0F0;
      case BlockId.SANDSTONE:   return 0xC4A050;
      case BlockId.CACTUS:      return 0x2A7A10;
      case BlockId.PLANKS:      return 0xB89050;
      case BlockId.COBBLESTONE: return 0x7A7A7A;
      case BlockId.GLASS:       return 0xC8E6FF;
      case BlockId.WHEAT:       return 0xD4AA30;
      case BlockId.DOOR_CLOSED: return 0x8B5E3C;
      case BlockId.DOOR_OPEN:   return 0x8B5E3C;
      case BlockId.BEDROCK:     return 0x3A3A3A;
      default:                  return 0x888888;
    }
  }

  /**
   * Get tool color based on material type
   * @param {string} material - Tool material name
   * @returns {number} - Hex color value
   */
  _getToolColor(material) {
    switch (material) {
      case 'wood':    return 0x6B4F2E;
      case 'stone':   return 0x8A8A8A;
      case 'iron':    return 0xD8D8D8;
      case 'gold':    return 0xFFD700;
      case 'diamond': return 0x4AEDD9;
      default:        return 0x888888;
    }
  }

  /**
   * Dispose all GPU resources
   */
  dispose() {
    this.clear();
    this._geometry.dispose();
  }
}
